/* /professional/register — Self-onboarding form for design professionals.
 *
 * Single-page flow. On submit, account enters `pending` status until
 * Team Mera Naksha verifies it via the admin dashboard. Professional is
 * told to login (phone+OTP) after they receive the verification SMS.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck,
  Sparkles,
  ChevronRight,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { CATEGORY_BY_KEY } from "@/professionals/categories";
import {
  MITRA_BUCKETS,
  MITRA_BUCKET_BY_KEY,
  MITRA_BUCKET_BY_SUBCATEGORY,
  MITRA_SUBCATEGORY_LABEL,
} from "@/mitras/mitraCategories";
import RegionPicker from "@/mitras/RegionPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErr = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const ProfessionalRegister = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    category: "architect",
    phone: "",
    email: "",
    state_code: "",
    city: "",
    state: "",
    experience_years: 5,
    bio: "",
    languages: "",
    service_areas: "",
    consultation_fee: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    document.title = "Join as a Professional · Mera Naksha";
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const meta = CATEGORY_BY_KEY[form.category];

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const phone = form.phone.replace(/\D+/g, "");
    if (!/^[6-9]\d{9}$/.test(phone.replace(/^91/, ""))) {
      setError("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    if (form.bio.trim().length < 10) {
      setError("Please write a short bio (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        category: form.category,
        phone,
        email: form.email.trim().toLowerCase(),
        city: form.city.trim(),
        state: form.state.trim(),
        // Hierarchical state code from RegionPicker — enables structured
        // SEO routes (`/mitras/design/up`) and the `?state_code=` filter
        // on /api/professionals. Optional: legacy free-text submissions
        // before the RegionPicker rollout don't carry this.
        state_code: form.state_code || undefined,
        experience_years: Number(form.experience_years) || 0,
        bio: form.bio.trim(),
        languages: form.languages
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
        service_areas: form.service_areas
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
        consultation_fee: form.consultation_fee.trim() ? Number(form.consultation_fee) : undefined,
      };
      const { data } = await axios.post(`${API}/professionals/register`, payload);
      setSuccess(data);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail, "Could not submit registration."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="pro-register-page">
      <header className="bg-navy text-white border-b-4 border-gold">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/professionals" data-testid="pro-register-back" className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm">
            <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="pro-register-brand-logo" />
          </Link>
          <Link to="/professional/login" data-testid="pro-register-cta-login" className="text-[12.5px] font-bold text-white/80 hover:text-gold">
            Already registered? Sign in →
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold-dark text-[10.5px] font-bold tracking-widest uppercase">
          <Sparkles className="h-3 w-3" /> Join as a Professional
        </div>
        <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">
          Architecture, Engineering &amp; Interior Support
        </h1>
        <p className="mt-1.5 text-[14px] text-muted-foreground max-w-2xl">
          Tell us about your practice. Once verified by Team Mera Naksha, your
          profile goes live across Bharat and qualified inquiries come straight
          to you.
        </p>

        {success ? (
          <section
            className="mt-6 rounded-3xl bg-white border-2 border-emerald-300 shadow-sm overflow-hidden"
            data-testid="pro-register-success"
          >
            <div className="bg-emerald-50 px-5 py-4 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-700" />
              <div className="font-extrabold text-emerald-900">Registration received</div>
            </div>
            <div className="p-5 sm:p-7 space-y-3">
              <p className="text-[14px] text-foreground">
                Thanks for applying, <strong>{form.full_name}</strong>. Our team will verify your details
                within 48 hours and notify you on <strong>+91 {form.phone}</strong>.
              </p>
              <p className="text-[13px] text-muted-foreground">
                Reference no: <span className="font-mono font-bold">{success.id}</span> · Status:{" "}
                <span className="font-bold text-yellow-700 uppercase">{success.status}</span>
              </p>
              <button
                type="button"
                onClick={() => navigate("/professional/login")}
                data-testid="pro-register-go-login"
                className="min-h-[44px] px-5 rounded-xl bg-navy text-gold font-extrabold text-[13.5px] inline-flex items-center gap-1.5"
              >
                Continue to Professional Login <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mt-6 bg-white rounded-3xl border-2 border-border shadow-sm p-5 sm:p-7 space-y-5"
            data-testid="pro-register-form"
          >
            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-red-50 border-2 border-red-200 px-3 py-2 text-[13px] text-red-800 font-semibold" data-testid="pro-register-error" role="alert">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Field label="Mitra Category" required>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="pro-register-bucket-picker">
                {MITRA_BUCKETS.map((b) => {
                  const active = (MITRA_BUCKET_BY_SUBCATEGORY[form.category] || "design") === b.key;
                  return (
                    <button
                      type="button"
                      key={b.key}
                      onClick={() => {
                        // Snap to the first sub-category of the chosen bucket.
                        const firstSub = b.subCategoryKeys[0];
                        setForm((f) => ({ ...f, category: firstSub }));
                      }}
                      data-testid={`pro-register-bucket-${b.key}`}
                      aria-pressed={active}
                      className={`flex flex-col items-start gap-1 text-left rounded-xl border-2 p-3 transition-all ${
                        active
                          ? "bg-navy text-white border-navy"
                          : "bg-white text-navy border-border hover:border-navy"
                      }`}
                    >
                      <b.Icon className="h-4 w-4" />
                      <span className="font-extrabold text-[12.5px] leading-tight">
                        {b.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Profession / Speciality" required>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                data-testid="pro-register-subcategory"
                required
                className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy font-bold"
              >
                {(() => {
                  const bucketKey = MITRA_BUCKET_BY_SUBCATEGORY[form.category] || "design";
                  const bucket = MITRA_BUCKET_BY_KEY[bucketKey];
                  return (bucket?.subCategoryKeys || []).map((sub) => (
                    <option key={sub} value={sub}>
                      {MITRA_SUBCATEGORY_LABEL[sub] || sub}
                    </option>
                  ));
                })()}
              </select>
              <div className="mt-1 text-[11.5px] text-muted-foreground">
                Pick the specific service you offer within the{" "}
                {MITRA_BUCKET_BY_KEY[MITRA_BUCKET_BY_SUBCATEGORY[form.category] || "design"]?.label || "Mitra"}{" "}
                ecosystem.
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input
                  required
                  value={form.full_name}
                  onChange={setField("full_name")}
                  maxLength={80}
                  data-testid="pro-register-name"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </Field>
              <Field label="Mobile number" required>
                <div className="flex items-center gap-2 h-11 rounded-xl border-2 border-border bg-white overflow-hidden focus-within:border-navy">
                  <span className="pl-3 pr-1 text-[14px] font-bold text-navy tabular-nums">+91</span>
                  <input
                    required
                    inputMode="numeric"
                    value={form.phone}
                    onChange={setField("phone")}
                    maxLength={14}
                    data-testid="pro-register-phone"
                    className="flex-1 h-full bg-transparent text-[14px] tabular-nums outline-none"
                    placeholder="98765 43210"
                  />
                </div>
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  data-testid="pro-register-email"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </Field>
              <Field label="Years of experience" required>
                <input
                  required
                  type="number"
                  min={0}
                  max={80}
                  value={form.experience_years}
                  onChange={setField("experience_years")}
                  data-testid="pro-register-experience"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </Field>
              <Field label="State & District" required>
                <RegionPicker
                  stateCode={form.state_code}
                  district={form.city}
                  onChange={({ state_code, state_name, district }) =>
                    setForm((f) => ({
                      ...f,
                      state_code,
                      state: state_name,
                      // We store the picked district in `city` to stay
                      // schema-compatible with the existing pro listing.
                      city: district,
                    }))
                  }
                  required
                  testIdPrefix="pro-register-region"
                />
              </Field>
            </div>

            <Field label={`About / bio — what makes you the right ${meta?.label || "professional"}?`} required>
              <textarea
                required
                rows={4}
                minLength={10}
                maxLength={2000}
                value={form.bio}
                onChange={setField("bio")}
                data-testid="pro-register-bio"
                placeholder="Briefly describe your experience, signature work, and the type of homes you specialise in."
                className="w-full px-3 py-2 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy resize-y"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Languages (comma separated)">
                <input
                  value={form.languages}
                  onChange={setField("languages")}
                  data-testid="pro-register-languages"
                  placeholder="Hindi, English, Marathi"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </Field>
              <Field label="Service areas (comma separated cities)">
                <input
                  value={form.service_areas}
                  onChange={setField("service_areas")}
                  data-testid="pro-register-service-areas"
                  placeholder="Patna, Ara, Buxar"
                  className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </Field>
            </div>

            <Field label="Consultation fee (optional, ₹)">
              <input
                type="number"
                min={0}
                max={1000000}
                value={form.consultation_fee}
                onChange={setField("consultation_fee")}
                data-testid="pro-register-fee"
                placeholder="1500"
                className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="pro-register-submit"
              className="w-full min-h-[52px] rounded-2xl bg-gold text-navy font-extrabold text-[14.5px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" /> Submit for verification
                </>
              )}
            </button>
            <p className="text-[11.5px] text-muted-foreground text-center leading-snug">
              By submitting, you agree to be verified by Team Mera Naksha. Your contact
              details remain private — inquiries are routed through us.
            </p>
          </form>
        )}
      </main>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">
      {label} {required && <span className="text-red-600">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default ProfessionalRegister;
